/**
 * Shared favourites. Both people favourite independently, which is what makes
 * "you both chose this one" mean something.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api.ts'
import type { Favorite, Gender, Member } from '@/lib/types.ts'

export type FavoriteGroup = {
  name: string
  gender: Gender
  /** Everyone who has favourited this name. */
  admirers: Member[]
  mutual: boolean
  firstAt: string
}

export function useFavorites(currentUserId: string | undefined) {
  const [favorites, setFavorites] = useState<Favorite[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const [fav, mem] = await Promise.all([api.favorites.list(), api.auth.members()])
    setFavorites(fav.favorites)
    setMembers(mem.members)
  }, [])

  useEffect(() => {
    refresh().catch(() => undefined).finally(() => setLoading(false))
  }, [refresh])

  const mineByName = useMemo(
    () => new Set(favorites.filter((f) => f.userId === currentUserId).map((f) => f.name.toLowerCase())),
    [favorites, currentUserId],
  )

  const isFavorite = useCallback((name: string) => mineByName.has(name.toLowerCase()), [mineByName])

  const toggle = useCallback(
    async (name: string, gender: Gender) => {
      if (!currentUserId) return
      const already = mineByName.has(name.toLowerCase())

      // Optimistic — favouriting should feel instant.
      setFavorites((prev) =>
        already
          ? prev.filter((f) => !(f.userId === currentUserId && f.name.toLowerCase() === name.toLowerCase()))
          : [...prev, { name, gender, userId: currentUserId, createdAt: new Date().toISOString() }],
      )

      try {
        if (already) await api.favorites.remove(name)
        else await api.favorites.add(name, gender)
      } catch {
        await refresh() // Put it back the way the server sees it.
      }
    },
    [currentUserId, mineByName, refresh],
  )

  /** One row per name, alphabetical, with everyone who loves it attached. */
  const groups = useMemo<FavoriteGroup[]>(() => {
    const byName = new Map<string, FavoriteGroup>()
    for (const fav of favorites) {
      const key = fav.name.toLowerCase()
      const member = members.find((m) => m.id === fav.userId)
      const existing = byName.get(key)
      if (existing) {
        if (member && !existing.admirers.some((a) => a.id === member.id)) existing.admirers.push(member)
        if (fav.createdAt < existing.firstAt) existing.firstAt = fav.createdAt
      } else {
        byName.set(key, {
          name: fav.name,
          gender: fav.gender,
          admirers: member ? [member] : [],
          mutual: false,
          firstAt: fav.createdAt,
        })
      }
    }

    const everyone = Math.max(members.length, 2)
    return [...byName.values()]
      .map((g) => ({ ...g, mutual: g.admirers.length >= everyone }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [favorites, members])

  return { groups, members, loading, isFavorite, toggle, refresh }
}
